import { useEffect, useState } from 'react';
import useDebounce from './use-debounce';
import { groupBy, uniqBy } from 'lodash';
import { SEARCH_OPTION_AGENTS, SEARCH_OPTION_HOME_APPRAISAL } from 'themes/themeConfig';
import { provinceOrStateNameFromCode, provinceAndStateCodes, ProvinceOrStateCode } from 'utils/province_or_state';
import { createQueryFunction as createQueryGoogleMapsFunction } from 'utils/google-maps';
import { getSearchPredictions, createSearchPredictionModel } from 'data/search-predictions';
import * as AgentActions from 'utils/agent-endpoint';
import { SearchStrategyFactory } from 'strategy/search';
import { useThemeContext, useUser, useFeaturesContext } from 'contexts';
import { useSearchOptions } from './use-search-options';

import type { SearchOptions } from 'themes/themeConfig';
import type SearchPrediction from 'data/search-predictions';
import type { SearchAgentPrediction } from 'components/suggested-location-dropdown';
import { AutocompletePredictionPredictionCategory, QuerySettingsType } from 'utils/google-maps/types';
import { getHomeAppraisalPredictions } from 'utils/google-maps/api/placePredictions';
import { ThemeNames } from 'types/themes';
import type { FeaturesType } from 'contexts/features';

// Feature flag to toggle between old implementation and new Strategy Pattern
const USE_SEARCH_STRATEGY_PATTERN = true;

// This is temporary fix to avoid showing provinces/states incorrectly marked as cities
// Should be removed after Province/State ticket is done
// Helper function to check if a prediction is a province/state incorrectly marked as a city
export const isProvinceOrStateIncorrectlyMarkedAsCity = (prediction: SearchPrediction): boolean => {
  if (prediction.label !== 'city') return false;

  const parts = prediction.description.split(',');
  const cityName = parts[0].trim().toLowerCase();
  const region = parts.length > 1 ? parts[1].trim().toLowerCase() : '';

  // Build the province name lookup
  const provinceNameSet = new Set(
    provinceAndStateCodes.map(code => provinceOrStateNameFromCode(code).toLowerCase())
  );

  // Allow Quebec and New York to be shown as cities and
  // treat them as cities in the transitionToPath()., there is special case for them
  if (cityName === 'quebec' || cityName === 'québec' || cityName === 'new york') {
    return false;
  }

  // Is the city name itself a province name?
  const isProvinceName = provinceNameSet.has(cityName);

  // If not a province name, it's a legitimate city
  if (!isProvinceName) return false;

  // Get province code from province name
  const provinceName = provinceOrStateNameFromCode(region as ProvinceOrStateCode).toLowerCase();
  if (provinceName && cityName === provinceName) {
    return true;
  }

  return false;
};

export enum searchProvider {
  GOOGLE = 'google',
  GO_SEARCH = 'go-search',
}

interface UseLocationSearchProps {
  queryString: string;
  useUsListings?: boolean;
  searchOptions?: SearchOptions;
  features?: FeaturesType;
}

export default function useLocationSearch({ queryString, useUsListings = false, searchOptions, features: propFeatures }: UseLocationSearchProps) {
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingSearchPredictions, setIsLoadingSearchPredictions] = useState(false);
  const [searchPredictions, setSearchPredictions] = useState<SearchPrediction[]>([]);
  const [searchAgentPredictions, setSearchAgentPredictions] = useState<SearchAgentPrediction[]>([]);
  const { siteLocation } = useUser();
  const { themeName } = useThemeContext();
  const { features: contextFeatures } = useFeaturesContext();  
  const features = propFeatures || contextFeatures;
  // Apply tenant-specific search options
  const effectiveSearchOptions = useSearchOptions(searchOptions);
  
  // Use different debounced functions based on the feature flag
  const debouncedSearch = useDebounce(
    USE_SEARCH_STRATEGY_PATTERN ? () => searchWithStrategy() : () => searchOriginal(useUsListings), 
    400
  );

  // Original implementation
  const uniqByDescriptionAndAddLocationId = (predictions: SearchPrediction[]) => {
    const resultMap: { [description: string]: SearchPrediction } = {};
    for (const prediction of predictions) {
      const descriptionKey = prediction.description.toLowerCase();
      if (descriptionKey in resultMap) {
        const existingLocation = resultMap[descriptionKey];
        if (prediction.source === searchProvider.GOOGLE) {
          existingLocation.locationId = prediction.id;
          existingLocation.source = prediction.source;
        }
      } else {
        resultMap[descriptionKey] = { ...prediction } as SearchPrediction;
      }
    }
    return Object.values(resultMap);
  };

  const modelingCities = (cities: SearchPrediction[]) => cities.map(createSearchPredictionModel);

  const googleQuerySettings: QuerySettingsType = {
    limits: {
      city: 10, neighborhood: 0, street: 10,
    },
  };

  // Original search implementation
  const searchOriginal = async (useUsListings: boolean) => {
    const performSearch = queryString.length > 2;
    const performReset = queryString.length === 0;
    if (performSearch) {
      setIsLoadingSearchPredictions(true);
      const isAgentSearch = searchOptions === SEARCH_OPTION_AGENTS;
      const isHomeAppraisalSearch = searchOptions === SEARCH_OPTION_HOME_APPRAISAL;
      if (isAgentSearch) {
        const data = await AgentActions.locationSuggestions(queryString, siteLocation);
        const searchAgentPredictions = data.map((i, index) => ({ identifier: index, label: i, group: 'locations' }));
        setSearchPredictions([]);
        setSearchAgentPredictions(searchAgentPredictions);
      } else {
        // Here we are getting the search predictions from go-search
        let goSearchPredictions = await getSearchPredictions({ query: queryString.toLowerCase(), country : themeName !== ThemeNames.ZOOCASA ? siteLocation : '' }, features) as SearchPrediction[];
        try {
          let googleSearchPredictions;
          // In results for home appraisal we need only address not cities
          if (isHomeAppraisalSearch) {
            googleSearchPredictions = await getHomeAppraisalPredictions(queryString, siteLocation);
          } else {
            googleSearchPredictions = await createQueryGoogleMapsFunction((useUsListings && themeName === 'zoocasa'), siteLocation)(queryString, googleQuerySettings);
            // If isHomeAppraisalSearch is true and there are more then 10 results, do not show searchPredictions
            // Show searchPredictions only if there are less then 10 results and do not show more then 10 in total
            if (isHomeAppraisalSearch) {
              if (googleSearchPredictions.length > 9) {
                goSearchPredictions = [];
                googleSearchPredictions = googleSearchPredictions.slice(0, 10);
              } else {
                const remainingSpaceForPredictions = 10 - googleSearchPredictions.length;
                // Remove cities from searchPredictions we received from go-search
                goSearchPredictions = searchPredictions.filter(p => p.label !== AutocompletePredictionPredictionCategory.CITY);
                goSearchPredictions = searchPredictions.slice(0, remainingSpaceForPredictions);
              }
            }

            const predictionsGroupedByGroup = groupBy([... goSearchPredictions, ...googleSearchPredictions], 'group');
            const { listings = [], schools = [], buildings = [], locations = []} = predictionsGroupedByGroup;

            const locationsGroupedByLabel = groupBy(locations, 'label');
            const { city = [], neighbourhood = [], street = []} = locationsGroupedByLabel;
            // Remove duplicate cities that could come from the go-search and preserve locationId that comes from Google
            const citiesWithLocationIds = uniqByDescriptionAndAddLocationId(city);
            const uniqCities = modelingCities(citiesWithLocationIds);
            // Remove duplicates that could come from the SearchPredictionStore
            const UNIQUENESS_ATTRIBUTE = 'description';
            const uniqNeighborhoods = uniqBy(neighbourhood, UNIQUENESS_ATTRIBUTE);
            const uniqStreets = uniqBy(street, UNIQUENESS_ATTRIBUTE);

            /*
              Filter out provinces/states incorrectly marked as cities
              This is a temporary fix to avoid showing provinces/states incorrectly marked as cities
              As clicking on these predictions shows area page we strange behavior (message "No results found" with listings below)
              I'v created a ticket to decide whether we want State/Province in our predictions or not
              Both solution include the go-search update to mark them properly or exclude them from predictions
              https://coda.io/d/Zoocasa-eXprealty-com_dT-0uNbKbyy/SD-Score-list_su2mVSiY#View-4-of-Tickets-db_tuQP_I9s/r767&view=center
            */
            const filteredCities = uniqCities.filter(city => !isProvinceOrStateIncorrectlyMarkedAsCity(city));

            let finalPredictions: SearchPrediction[] = [];

            if (isHomeAppraisalSearch) {
              finalPredictions = [
                ...listings,
                ...filteredCities,
                ...uniqStreets,
              ];
            } else {
              finalPredictions = [
                ...listings,
                ...schools,
                ...buildings,
                ...filteredCities,
                ...uniqNeighborhoods,
                ...uniqStreets,
              ];
            }

            setSearchPredictions(finalPredictions);
            setSearchAgentPredictions([]);
          }

          // If isHomeAppraisalSearch is true and there are more then 10 results, do not show searchPredictions
          // Show searchPredictions only if there are less then 10 results and do not show more then 10 in total
          if (isHomeAppraisalSearch) {
            if (googleSearchPredictions.length > 9) {
              goSearchPredictions = [];
              googleSearchPredictions = googleSearchPredictions.slice(0, 10);
            } else {
              const remainingSpaceForPredictions = 10 - googleSearchPredictions.length;
              // Remove cities from searchPredictions we received from go-search
              goSearchPredictions = searchPredictions.filter(p => p.label !== AutocompletePredictionPredictionCategory.CITY);
              goSearchPredictions = searchPredictions.slice(0, remainingSpaceForPredictions);
            }
          }

          const predictionsGroupedByGroup = groupBy([... goSearchPredictions, ...googleSearchPredictions], 'group');
          const { listings = [], schools = [], buildings = [], locations = []} = predictionsGroupedByGroup;

          const locationsGroupedByLabel = groupBy(locations, 'label');
          const { city = [], neighbourhood = [], street = []} = locationsGroupedByLabel;
          // Remove duplicate cities that could come from the go-search and preserve locationId that comes from Google
          const citiesWithLocationIds = uniqByDescriptionAndAddLocationId(city);
          const uniqCities = modelingCities(citiesWithLocationIds);
          // Remove duplicates that could come from the SearchPredictionStore
          const UNIQUENESS_ATTRIBUTE = 'description';
          const uniqNeighborhoods = uniqBy(neighbourhood, UNIQUENESS_ATTRIBUTE);
          const uniqStreets = uniqBy(street, UNIQUENESS_ATTRIBUTE);

          let finalPredictions: SearchPrediction[] = [];

          if (isHomeAppraisalSearch) {
            finalPredictions = [
              ...listings,
              ...uniqCities,
              ...uniqStreets,
            ];
          } else {
            finalPredictions = [
              ...listings,
              ...schools,
              ...buildings,
              ...uniqCities,
              ...uniqNeighborhoods,
              ...uniqStreets,
            ];
          }

          setSearchPredictions(finalPredictions);
          setSearchAgentPredictions([]);
        } catch (err: any) {
          console.error(err);
        }
      }
      setIsTyping(false);
      setIsLoadingSearchPredictions(false);
    } else if (performReset) {
      setIsTyping(false);
      setSearchPredictions([]);
      setSearchAgentPredictions([]);
      setIsLoadingSearchPredictions(false);
    }
  };

  // New search implementation using Strategy Pattern
  const searchWithStrategy = async () => {
    const performSearch = queryString.length > 2;
    const performReset = queryString.length === 0;
    
    if (performSearch) {
      setIsLoadingSearchPredictions(true);
      
      try {
        // Get the appropriate search strategy based on effectiveSearchOptions
        const searchStrategy = SearchStrategyFactory.getStrategy(effectiveSearchOptions);
        // Execute the search using the selected strategy
        const { searchPredictions: predictions, searchAgentPredictions: agentPredictions } = 
          await searchStrategy.search({
            queryString,
            useUsListings,
            siteLocation,
            themeName,
            features, 
          });
          
        setSearchPredictions(predictions);
        setSearchAgentPredictions(agentPredictions);
      } catch (err: any) {
        console.error(err);
        setSearchPredictions([]);
        setSearchAgentPredictions([]);
      }
      
      setIsTyping(false);
      setIsLoadingSearchPredictions(false);
    } else if (performReset) {
      setIsTyping(false);
      setSearchPredictions([]);
      setSearchAgentPredictions([]);
      setIsLoadingSearchPredictions(false);
    }
  };

  useEffect(() => {
    debouncedSearch();
    !!queryString.length && setIsTyping(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, effectiveSearchOptions]);

  return { isLoadingSearchPredictions, searchPredictions, searchAgentPredictions, isTyping };
}